import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Collapse, Input, Modal, Space, Spin, Statistic, Table, Tag, message } from 'antd';
import NonStdWorkbench from '../../../nonstandard-ops/NonStdWorkbench';
import LabelPrintModal from '../../../nonstandard-ops/LabelPrintModal';
import NonStdSnapshotView from '../../../nonstandard-ops/NonStdSnapshotView';
import DestroyRecordsPanel from '../../../nonstandard-ops/DestroyRecordsPanel';
import {
  ConsumedTag, LabelGroupSections, LabelSection, consumedRowStyle, hasGroups, hasSizeInfo, sizeWeightCell,
} from '../../../nonstandard-ops/labelGroups';
import {
  batchNonStdLabelConsume, batchNonStdLabelPlacement, getNonStdSummary, saveNonStdConfirmNote, updateNonStdLabelPlacement,
} from '../../../../services/nonstandardOps';
import { NonStdLabel, NonStdPlacementPayload, NonStdSummary } from '../../../../types/nonstandardOps';

type Props = {
  instructionDetail?: any;
  step?: any;
  /** 上报容器核对进度和销毁情况是否已回答：页面据此禁用/启用右上角的"提交记录并完成本阶段"（单独存一份，不放进会被重新加载覆盖的步骤草稿） */
  onNonstdConfirmGate?: (gate: { pending: number; total: number; unlabeled: number; no_location: number; destroy_unanswered: boolean }) => void;
};

const LOCATION_MAX = 100;
const NOTE_MAX = 2000;

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

/**
 * 库存非标准操作 · 确认完成（最后一步）：补充说明（选填）、确认销毁情况（有销毁记录，或勾选没有发生销毁）、核对容器是否都贴了标签、记录每个容器放在哪里。
 * 每个容器必须是"已就位"（已贴标签且有位置）、"未使用"（写明原因）或"已消耗"（容器里的东西已被二次加工、容器已不存在），否则不能提交本阶段；
 * 提交用页面右上角所有模板通用的"提交记录并完成本阶段"，后端会再校验一次。位置只是手填的文字，不接入仓位表。
 */
const NonStdSubmitRenderer: React.FC<Props> = ({ instructionDetail, step, onNonstdConfirmGate }) => {
  const instructionId = Number(instructionDetail?.id || 0);
  const [summary, setSummary] = useState<NonStdSummary | null>(null);
  const [error, setError] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteSaved, setNoteSaved] = useState('');
  const [noteState, setNoteState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [locDraft, setLocDraft] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkLocation, setBulkLocation] = useState('');
  const [unusedTarget, setUnusedTarget] = useState<NonStdLabel | null>(null);
  const [unusedReason, setUnusedReason] = useState('');
  const [reprint, setReprint] = useState<NonStdLabel | null>(null);
  const noteInitRef = useRef(false);
  // 正在保存的容器：回车保存后紧接着失焦，不重复发请求
  const savingRef = useRef<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      const data = await getNonStdSummary(instructionId);
      setSummary(data);
      setError('');
      if (!noteInitRef.current) {
        noteInitRef.current = true;
        setNoteText(data.confirm?.note || '');
        setNoteSaved(data.confirm?.note || '');
      }
    } catch (err: any) {
      setError(errorText(err, '加载失败'));
    }
  }, [instructionId]);

  useEffect(() => { if (instructionId) load(); }, [instructionId, load]);

  const confirm = summary?.confirm;
  const canPlace = !!confirm?.can_place;
  const canConsume = !!confirm?.can_consume;

  // 把容器核对进度和销毁情况告诉页面：还有没处理完的容器、或销毁情况没回答时，页面右上角的"提交记录并完成本阶段"保持禁用
  const gatePending = confirm?.containers?.pending;
  const gateTotal = confirm?.containers?.total;
  const gateUnlabeled = confirm?.containers?.unlabeled;
  const gateNoLocation = confirm?.containers?.no_location;
  const destroyUnanswered = summary?.destroy ? !summary.destroy.answered : undefined;
  useEffect(() => {
    if (!onNonstdConfirmGate || gatePending === undefined || destroyUnanswered === undefined) return;
    onNonstdConfirmGate({
      pending: Number(gatePending || 0), total: Number(gateTotal || 0),
      unlabeled: Number(gateUnlabeled || 0), no_location: Number(gateNoLocation || 0),
      destroy_unanswered: destroyUnanswered,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatePending, gateTotal, gateUnlabeled, gateNoLocation, destroyUnanswered]);

  // 补充说明：输入停顿后自动保存，避免没点保存就直接点完成而丢失
  useEffect(() => {
    if (!canPlace || noteText === noteSaved) return undefined;
    const timer = setTimeout(async () => {
      setNoteState('saving');
      try {
        await saveNonStdConfirmNote(instructionId, noteText);
        setNoteSaved(noteText);
        setNoteState('saved');
      } catch (err: any) {
        setNoteState('error');
        message.error(errorText(err, '补充说明保存失败'));
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [noteText, noteSaved, canPlace, instructionId]);

  const changeLabel = async (label: NonStdLabel, payload: NonStdPlacementPayload) => {
    if (savingRef.current.has(label.id)) return;
    savingRef.current.add(label.id);
    try {
      await updateNonStdLabelPlacement(label.id, payload);
    } catch (err: any) {
      message.error(errorText(err, '保存失败'));
    } finally {
      savingRef.current.delete(label.id);
    }
    setLocDraft(prev => { const next = { ...prev }; delete next[label.id]; return next; });
    await load();
  };

  const commitLocation = (label: NonStdLabel) => {
    const draft = locDraft[label.id];
    if (draft === undefined || draft.trim() === (label.location || '')) {
      setLocDraft(prev => { const next = { ...prev }; delete next[label.id]; return next; });
      return;
    }
    changeLabel(label, { location: draft });
  };

  const bulk = async (body: { labeled?: boolean; location?: string }, doneText: string) => {
    if (selected.length === 0) return;
    try {
      await batchNonStdLabelPlacement(instructionId, { label_ids: selected, ...body });
      message.success(doneText);
    } catch (err: any) {
      message.error(errorText(err, '批量设置失败'));
    }
    await load();
  };

  /** 标记/取消"已消耗"（单个、选中的、整组都走这里） */
  const consume = async (ids: number[], consumed: boolean) => {
    if (ids.length === 0) return;
    try {
      const result = await batchNonStdLabelConsume(instructionId, { label_ids: ids, consumed });
      message.success(`${consumed ? '已标记为已消耗' : '已取消已消耗'}：${result.labels.length} 个${result.skipped_unused ? `（另有 ${result.skipped_unused} 个标记了“未使用”的容器已跳过）` : ''}`);
    } catch (err: any) {
      message.error(errorText(err, '操作失败'));
    }
    setSelected(prev => prev.filter(id => !ids.includes(id)));
    await load();
  };

  const askConsume = (ids: number[], what: string) => {
    Modal.confirm({
      title: `把${what}标记为“已消耗”？`,
      content: '“已消耗”表示容器里的东西已经被二次加工、这个容器已经不存在了：不再需要贴标签和记录位置，也不能再打印标签。标错了可以随时取消。',
      okText: '标记为已消耗',
      cancelText: '取消',
      onOk: () => consume(ids, true),
    });
  };

  const submitUnused = async () => {
    if (!unusedTarget) return;
    if (!unusedReason.trim()) {
      message.warning('请写明这个容器为什么没有使用');
      return;
    }
    await changeLabel(unusedTarget, { unused: true, unused_reason: unusedReason.trim() });
    setUnusedTarget(null);
    setUnusedReason('');
  };

  /** 分组标题右侧的整组操作：整组标记/取消已消耗 */
  const renderSectionActions = (section: LabelSection<NonStdLabel>) => {
    if (!canConsume) return null;
    const consumable = section.rows.filter(canConsumeRow);
    const consumedRows = section.rows.filter(row => row.consumed);
    const groupLabel = section.name ? `“${section.name}”组` : '“未分组”';
    return (
      <>
        {consumable.length > 0 ? <a onClick={() => askConsume(consumable.map(row => row.id), `${groupLabel}的 ${consumable.length} 个容器`)}>整组标记已消耗</a> : null}
        {consumedRows.length > 0 ? <a onClick={() => consume(consumedRows.map(row => row.id), false)}>整组取消已消耗</a> : null}
      </>
    );
  };

  if (!instructionId) return null;
  // 本阶段已提交：显示提交时固化的快照（只读）
  if (String(step?.status || '') === 'done' && step?.collected_metrics?.nonstd_snapshot) {
    return <NonStdSnapshotView snapshot={step.collected_metrics.nonstd_snapshot} />;
  }
  if (error && !summary) return <Alert type="error" showIcon message={error} />;
  if (!summary || !confirm) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;

  const labels = summary.labels || [];
  const counts = confirm.containers;
  const entryCount = summary.entries.filter(e => e.kind === 'note').length;
  const rounds = summary.rounds?.length || 0;
  const pendingIds = labels.filter(l => l.placement_status === 'pending').map(l => l.id);
  const canConsumeRow = (l: NonStdLabel) => !l.consumed && !l.unused;
  const selectedLabels = labels.filter(l => selected.includes(l.id));
  const sizeColumn = hasSizeInfo(labels);

  const blocker = counts.total === 0
    ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="本次没有生成容器，无需核对。" />
    : counts.pending > 0
      ? <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`还有 ${counts.pending} 个容器没有处理完（${counts.unlabeled} 个没有标记“已贴标签”，${counts.no_location} 个没有记录放置位置），处理完才能提交本阶段。标签打错、实际没有的容器请点“标记为未使用”并写明原因。`} />
      : summary.destroy && !summary.destroy.answered
        ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="所有容器都已处理。还需要确认上面的销毁情况，才能提交本阶段。" />
        : <Alert type="success" showIcon style={{ marginBottom: 12 }} message="所有容器都已处理。确认没问题后，点页面右上角的“提交记录并完成本阶段”，指令进入“待终审”。" />;

  return (
    <div style={{ width: '100%' }}>
      <Card size="small" title="确认完成" style={{ marginBottom: 12 }}>
        <Space size={32} wrap>
          <Statistic title="执行记录" value={entryCount} suffix="条" />
          <Statistic title="轮次" value={rounds} suffix="轮" />
          <Statistic title="容器" value={counts.total} suffix="个" />
          <Statistic title="已就位" value={counts.placed} suffix={`/ ${counts.total - counts.unused - (counts.consumed || 0)}`} />
          {counts.consumed > 0 ? <Statistic title="已消耗" value={counts.consumed} suffix="个" /> : null}
        </Space>
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="这是最后一步：补充需要说明的信息（选填），核对容器是否都贴好了标签，并手动记录每个容器放在了哪里。全部处理完，点页面右上角的“提交记录并完成本阶段”，指令进入“待终审”。"
        />
        {!confirm.record_done ? (
          <Alert type="warning" showIcon style={{ marginTop: 12 }} message="“执行记录”阶段还没有提交，请先回到上一阶段点“提交记录并完成本阶段”，再回来核对容器。" />
        ) : null}
        {confirm.submit_done ? (
          <Alert type="info" showIcon style={{ marginTop: 12 }} message="本阶段已经提交，下面的内容只读。" />
        ) : null}
      </Card>

      <Card size="small" title="补充说明（选填）" style={{ marginBottom: 12 }}>
        <Input.TextArea
          rows={3}
          maxLength={NOTE_MAX}
          showCount
          disabled={!canPlace}
          value={noteText}
          placeholder="有需要补充的信息就写在这里，例如：有一箱需要客户确认后再上架。不需要可以留空。只给仓库内部和管理员看，不会显示给客户。"
          onChange={e => { setNoteText(e.target.value); setNoteState('idle'); }}
        />
        <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
          {noteText !== noteSaved ? '输入后会自动保存' : noteState === 'saved' ? '已保存' : ' '}
        </div>
      </Card>

      {summary.destroy ? (
        <Card size="small" className="nonstd-destroy-card" title={`销毁记录${summary.destroy.records.length > 0 ? `（${summary.destroy.records.length}）` : ''}`} style={{ marginBottom: 12 }}>
          <DestroyRecordsPanel instructionId={instructionId} info={summary.destroy} askDeclaration onChanged={load} />
        </Card>
      ) : null}

      <Card size="small" title={`容器核对（${counts.total}）`} style={{ marginBottom: 12 }}>
        {blocker}
        {labels.length === 0 ? null : (
          <>
            <Space wrap style={{ marginBottom: 8 }}>
              <Button disabled={!canPlace || pendingIds.length === 0} onClick={() => setSelected(pendingIds)}>选中所有待处理</Button>
              <Button disabled={!canPlace || selected.length === 0} onClick={() => bulk({ labeled: true }, '已标记为已贴标签')}>选中的标记为已贴标签</Button>
              {canConsume ? (
                <Button disabled={!selectedLabels.some(canConsumeRow)} onClick={() => askConsume(selectedLabels.filter(canConsumeRow).map(l => l.id), `选中的 ${selectedLabels.filter(canConsumeRow).length} 个容器`)}>选中的标记已消耗</Button>
              ) : null}
              {canConsume ? (
                <Button disabled={!selectedLabels.some(l => l.consumed)} onClick={() => consume(selectedLabels.filter(l => l.consumed).map(l => l.id), false)}>选中的取消已消耗</Button>
              ) : null}
              <Input
                style={{ width: 240 }}
                placeholder="给选中的容器设置同一个位置"
                maxLength={LOCATION_MAX}
                disabled={!canPlace}
                value={bulkLocation}
                onChange={e => setBulkLocation(e.target.value)}
              />
              <Button
                disabled={!canPlace || selected.length === 0 || !bulkLocation.trim()}
                onClick={async () => { await bulk({ location: bulkLocation.trim() }, '已设置位置'); setBulkLocation(''); }}
              >
                设置位置
              </Button>
            </Space>
            <LabelGroupSections
              rows={labels}
              renderExtra={renderSectionActions}
              renderTable={rows => (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={rows}
              // 分组后每组一张表，固定列宽才能让各组的列对齐
              tableLayout={hasGroups(labels) ? 'fixed' : undefined}
              onRow={row => ({ style: consumedRowStyle(row) })}
              rowSelection={canPlace || canConsume ? {
                // 每个分组一张表：这张表只管自己那几行，别的表里已选中的保持不变
                selectedRowKeys: selected.filter(id => rows.some(row => row.id === id)),
                onChange: keys => setSelected(prev => [...prev.filter(id => !rows.some(row => row.id === id)), ...(keys as number[])]),
              } : undefined}
              columns={[
                { title: '编号', dataIndex: 'number', width: 70, render: (v: string) => (v ? <b>{v}</b> : '-') },
                { title: '内容物', dataIndex: 'contents', render: (v: string) => v || '-' },
                { title: '备注', dataIndex: 'remark', width: 160, render: (v: string) => v || '-' },
                ...(sizeColumn ? [{ title: '尺寸/重量', width: 170, render: (_: any, row: NonStdLabel) => sizeWeightCell(row) }] : []),
                {
                  title: '标签',
                  width: 120,
                  render: (_: any, row: NonStdLabel) => (
                    <div style={{ fontSize: 12 }}>
                      <div>{row.print_count > 0 ? `已打印 ${row.print_count} 张` : <Tag>未打印</Tag>}</div>
                      {summary.can_print_labels ? (row.consumed
                        ? <span style={{ color: '#bfbfbf', cursor: 'not-allowed' }} title="已消耗的标签不能打印（容器已不存在）；如果标错了，先取消“已消耗”">{row.print_count > 0 ? '重打' : '打印'}</span>
                        : <a onClick={() => setReprint(row)}>{row.print_count > 0 ? '重打' : '打印'}</a>) : null}
                    </div>
                  ),
                },
                {
                  title: '已贴标签',
                  width: 90,
                  render: (_: any, row: NonStdLabel) => (row.consumed ? <span style={{ color: '#8c8c8c' }}>-</span> : (
                    <Checkbox checked={!!row.labeled} disabled={!canPlace || !!row.unused} onChange={e => changeLabel(row, { labeled: e.target.checked })} />
                  )),
                },
                {
                  title: '放置位置',
                  width: 240,
                  render: (_: any, row: NonStdLabel) => (row.unused || row.consumed ? <span style={{ color: '#8c8c8c' }}>-</span> : (
                    <Input
                      value={locDraft[row.id] ?? row.location ?? ''}
                      maxLength={LOCATION_MAX}
                      disabled={!canPlace}
                      placeholder="例如 A-03-2，或靠门口的临时桌子"
                      onChange={e => setLocDraft(prev => ({ ...prev, [row.id]: e.target.value }))}
                      onBlur={() => commitLocation(row)}
                      onPressEnter={() => commitLocation(row)}
                    />
                  )),
                },
                {
                  title: '状态',
                  width: 150,
                  render: (_: any, row: NonStdLabel) => {
                    if (row.consumed) {
                      return (
                        <div style={{ fontSize: 12 }}>
                          <ConsumedTag />
                          {row.consumed_by_name ? <div style={{ color: '#8c8c8c' }}>{row.consumed_by_name}</div> : null}
                          {canConsume ? <a onClick={() => consume([row.id], false)}>取消已消耗</a> : null}
                        </div>
                      );
                    }
                    if (row.unused) {
                      return (
                        <div style={{ fontSize: 12 }}>
                          <Tag>未使用</Tag>
                          <div style={{ color: '#8c8c8c' }}>{row.unused_reason}</div>
                          {canPlace ? <a onClick={() => changeLabel(row, { unused: false })}>恢复使用</a> : null}
                        </div>
                      );
                    }
                    return (
                      <div style={{ fontSize: 12 }}>
                        {row.placement_status === 'placed' ? <Tag color="green">已就位</Tag> : <Tag color="orange">待处理</Tag>}
                        {canPlace ? <div><a onClick={() => { setUnusedTarget(row); setUnusedReason(''); }}>标记为未使用</a></div> : null}
                        {canConsume ? <div><a onClick={() => consume([row.id], true)}>标记已消耗</a></div> : null}
                      </div>
                    );
                  },
                },
              ]}
            />
              )}
            />
          </>
        )}
      </Card>

      <Collapse
        size="small"
        items={[{
          key: 'records',
          label: '查看执行记录（只读，供核对）',
          children: <NonStdWorkbench instructionId={instructionId} mode="view" hideCharges hideLabels hideDestroy />,
        }]}
      />

      <Modal
        title="标记为未使用"
        open={!!unusedTarget}
        okText="标记为未使用"
        cancelText="取消"
        onOk={submitUnused}
        onCancel={() => setUnusedTarget(null)}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          容器 {unusedTarget?.number ? `“${unusedTarget.number}”` : ''}{unusedTarget?.contents ? `（${unusedTarget.contents}）` : ''} 不再需要核对。请写明原因：
        </div>
        <Input.TextArea rows={3} maxLength={300} value={unusedReason} placeholder="例如：标签打错了，实际没有这个容器" onChange={e => setUnusedReason(e.target.value)} />
      </Modal>

      <LabelPrintModal
        open={!!reprint}
        instructionId={instructionId}
        instructionCode={summary.instruction.system_code}
        mode="print"
        labels={reprint ? [reprint] : []}
        onClose={() => setReprint(null)}
        onChanged={load}
      />
    </div>
  );
};

export default NonStdSubmitRenderer;
